import * as amplitude from '@amplitude/analytics-browser';

let initialized = false;
let gaInitialized = false;
let globalErrorTrackingInstalled = false;
let routeContextProvider = null;
const recentErrorCounts = new Map();
const ERROR_RATE_WINDOW_MS = 60 * 1000;
const MAX_ERROR_EVENTS_PER_KEY = 5;

let gaMeasurementId = null;

function gtag() {
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer && window.dataLayer.push(arguments);
}

function initGoogleAnalytics() {
  const measurementId = process.env.VUE_APP_GA_MEASUREMENT_ID;
  console.log('[Analytics] GA init called, measurement ID present:', !!measurementId);
  if (!measurementId) {
    console.warn('[Analytics] GA measurement ID not set (VUE_APP_GA_MEASUREMENT_ID)');
    return;
  }

  gaMeasurementId = measurementId;
  window.dataLayer = window.dataLayer || [];
  gtag('js', new Date());
  // We send page_view manually via the router so SPA navigations are tracked.
  gtag('config', measurementId, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);

  gaInitialized = true;
  console.log('[Analytics] Google Analytics initialized successfully');
}

function gaEvent(eventName, properties) {
  if (!gaInitialized) return;
  gtag('event', eventName, properties || {});
}

export function init(router) {
  initGoogleAnalytics();

  const apiKey = process.env.VUE_APP_AMPLITUDE_API_KEY;
  console.log('[Analytics] init called, API key present:', !!apiKey);
  if (apiKey) {
    amplitude.init(apiKey, {
      defaultTracking: { sessions: true, pageViews: false },
    });
    initialized = true;
    console.log('[Analytics] Amplitude initialized successfully');
  } else {
    console.warn('[Analytics] Amplitude API key not set (VUE_APP_AMPLITUDE_API_KEY)');
  }

  if (router) {
    router.afterEach((to, from) => {
      track('page_view', {
        path: to.path,
        full_path: to.fullPath,
        referrer: from.fullPath,
        title: document.title,
      });
      if (gaInitialized) {
        gtag('event', 'page_view', {
          page_path: to.fullPath,
          page_location: window.location.href,
          page_title: document.title,
          page_referrer: from.fullPath,
        });
      }
    });
  }
}

export function identify(user) {
  if (!user) return;

  const userId = user.model?.id || user.sub;

  const userProperties = {
    username: user.username || '',
    display_name: user.model?.displayName || '',
    email: user.model?.email || '',
    is_pro: !!user.model?.isPro,
    subscribers_count: user.model?.subscribersCount || 0,
    is_legacy_user: !!user.model?.legacyId,
    cognito_sub: user.sub || '',
    language: localStorage.getItem('preferredLanguage') || 'EN',
    theme: localStorage.getItem('darkMode') === 'true' ? 'dark' : 'light',
  };

  if (initialized) {
    amplitude.setUserId(userId);
    const identifyEvent = new amplitude.Identify();
    Object.entries(userProperties).forEach(([key, value]) => identifyEvent.set(key, value));
    amplitude.identify(identifyEvent);
  }

  if (gaInitialized) {
    gtag('config', gaMeasurementId, { user_id: userId, send_page_view: false });
    gtag('set', 'user_properties', userProperties);
  }
}

export function track(eventName, properties) {
  console.log('[Analytics] track:', eventName, properties);
  if (initialized) amplitude.track(eventName, properties);
  gaEvent(eventName, properties);
}

function truncate(value, maxLength = 500) {
  if (value == null) return '';
  const text = String(value);
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
}

function safeStringify(value) {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function normalizeError(error) {
  if (error instanceof Error) {
    return {
      name: error.name || 'Error',
      message: truncate(error.message),
      code: error.code || error.errorType || '',
      stack: truncate(error.stack, 1200),
    };
  }

  if (typeof error === 'string') {
    return {
      name: 'Error',
      message: truncate(error),
      code: '',
      stack: '',
    };
  }

  const graphQLError = error?.errors?.[0];
  return {
    name: error?.name || graphQLError?.errorType || 'Error',
    message: truncate(graphQLError?.message || error?.message || safeStringify(error || {})),
    code: error?.code || graphQLError?.errorType || '',
    stack: truncate(error?.stack || ''),
  };
}

function normalizeContext(context = {}) {
  const normalized = {};
  Object.entries(context || {}).forEach(([key, value]) => {
    if (value == null || typeof value === 'function') return;
    if (['password', 'token', 'authorization', 'content', 'message'].includes(String(key).toLowerCase())) return;

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      normalized[key] = typeof value === 'string' ? truncate(value, 250) : value;
      return;
    }

    normalized[key] = truncate(safeStringify(value), 500);
  });
  return normalized;
}

function shouldTrackError(errorInfo, context) {
  const now = Date.now();
  const key = [
    context.source || 'unknown',
    errorInfo.code || errorInfo.name,
    errorInfo.message,
    context.component || '',
  ].join('|');
  const existing = recentErrorCounts.get(key);
  if (!existing || now - existing.startedAt > ERROR_RATE_WINDOW_MS) {
    recentErrorCounts.set(key, { count: 1, startedAt: now });
    return true;
  }
  if (existing.count >= MAX_ERROR_EVENTS_PER_KEY) return false;
  existing.count += 1;
  return true;
}

export function captureError(error, context = {}) {
  if (!initialized && !gaInitialized) return;

  const errorInfo = normalizeError(error);
  const route = routeContextProvider?.();
  const safeContext = normalizeContext(context);

  if (!shouldTrackError(errorInfo, safeContext)) return;

  const payload = {
    source: safeContext.source || 'unknown',
    error_name: errorInfo.name,
    error_message: errorInfo.message,
    error_code: errorInfo.code || 'unknown',
    error_stack: errorInfo.stack,
    path: route?.path || window.location.pathname,
    full_path: route?.fullPath || window.location.pathname + window.location.search,
    user_agent: navigator.userAgent,
    ...safeContext,
  };

  if (initialized) amplitude.track('app_error', payload);

  if (gaInitialized) {
    gtag('event', 'exception', {
      description: `${errorInfo.name}: ${errorInfo.message}`,
      fatal: false,
      error_source: payload.source,
      error_code: payload.error_code,
      page_path: payload.full_path,
    });
  }
}

export function setupGlobalErrorTracking({ app, router } = {}) {
  routeContextProvider = () => router?.currentRoute?.value || null;
  if (globalErrorTrackingInstalled) return;
  globalErrorTrackingInstalled = true;

  if (app) {
    app.config.errorHandler = (error, instance, info) => {
      captureError(error, {
        source: 'vue_error_handler',
        component: instance?.$options?.name || instance?.$?.type?.name || '',
        info,
      });
      console.error(error);
    };
  }

  window.addEventListener('error', event => {
    captureError(event.error || event.message, {
      source: 'window_error',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', event => {
    captureError(event.reason, {
      source: 'unhandled_rejection',
    });
  });

  const originalConsoleError = console.error.bind(console);
  console.error = (...args) => {
    originalConsoleError(...args);
    captureError(args.find(arg => arg instanceof Error) || args[0], {
      source: 'console_error',
      detail: args.slice(1, 4).map(arg => truncate(typeof arg === 'string' ? arg : safeStringify(arg), 250)).join(' | '),
    });
  };
}

export function reset() {
  if (initialized) amplitude.reset();
  if (gaInitialized) {
    gtag('config', gaMeasurementId, { user_id: null, send_page_view: false });
    gtag('set', 'user_properties', {});
  }
}
